import { getBootcampStep } from "@Components/game/session-results/bootcamp/bootcamp-completion.config";
import { BootcampChallengeStep, resolveBootcampChallengeStep } from "@Constants/bootcamp";
import { Actions, Events, UnityBridge } from "@Context/common/general/context";
import ChallengeStatus from "@Enums/ChallengeStatus";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useCompletePlaySession from "@hooks/game/use-complete-play-session";
import type { ChallengeResponse } from "@Shared/schemas/challenges/challengeSchemas";
import { useEffect, useRef, useState } from "react";

interface BootcampCompletionResult {
  challenge: ChallengeResponse;
  challengeStep: BootcampChallengeStep;
  playSessionId: number;
  metricScore?: number;
  metricTarget?: number;
  completedSteps: number;
  failed: boolean;
}

interface UseBootcampSessionOptions {
  refreshChallenges: () => void;
}

const SESSION_SCORE_TO_BYB_IQ = 10;

const getCompletionMetricValues = (challengeStep: BootcampChallengeStep, results: any) => {
  const step = getBootcampStep(challengeStep);
  if (!step?.metric) {
    return { metricScore: undefined, metricTarget: undefined };
  }

  if (step.metric === MetricFilterTypes.BYB_IQ) {
    return {
      metricScore: results.byb_iq,
      metricTarget: (results.session_score?.target_score ?? 0) * SESSION_SCORE_TO_BYB_IQ,
    };
  }

  return {
    metricScore: results.scores?.[step.metric]?.final_score,
    metricTarget: results.score_goals?.[step.metric]?.target_score,
  };
};

const useBootcampSession = ({ refreshChallenges }: UseBootcampSessionOptions) => {
  const { globalState, dispatch } = useGlobalContext();
  const [completionResult, setCompletionResult] = useState<BootcampCompletionResult | null>(null);
  const [isWebInputBlocked, setIsWebInputBlocked] = useState(false);
  const activeChallengeRef = useRef<ChallengeResponse | null>(null);
  const activePlaySessionIdRef = useRef<number | null>(null);
  const completedStepsAtLaunchRef = useRef(0);
  const isCreatingSessionRef = useRef(false);
  const isMountedRef = useRef(true);

  const resetActiveSession = () => {
    activeChallengeRef.current = null;
    activePlaySessionIdRef.current = null;
  };

  useEffect(() => {
    isMountedRef.current = true;
    const unblockWebInput = () => setIsWebInputBlocked(false);
    globalState.app.events.gameplayEntered.uniq(unblockWebInput);

    return () => {
      isMountedRef.current = false;
      globalState.app.events.gameplayEntered.remove(unblockWebInput);
    };
  }, [globalState.app.events.gameplayEntered]);

  useEffect(() => () => Events.app.setBootcampHeaderAction.emit("skip"), []);

  const { isCompletingSession } = useCompletePlaySession({
    matches: (_, playSessionId) => playSessionId === activePlaySessionIdRef.current,
    onSuccess: (results, playSessionId) => {
      const challenge = activeChallengeRef.current;
      const challengeStep = resolveBootcampChallengeStep(challenge);
      activePlaySessionIdRef.current = null;
      setIsWebInputBlocked(false);

      if (!challenge || !challengeStep) {
        resetActiveSession();
        Events.app.navigate.emit({
          path: `/activity/session/${playSessionId}`,
          data: { tags: challenge?.tags, hideHeader: true },
          onNavigated: () => dispatch({ type: Actions.UsersActions.GetAccountInfo }),
        });
        return;
      }

      const failed = Boolean(results.failed);
      Events.app.setBootcampHeaderAction.emit(challengeStep === BootcampChallengeStep.FINAL && !failed ? "complete" : "skip");

      // Final success must keep inBootcamp true so this screen stays mounted for the dialog.
      if (challengeStep !== BootcampChallengeStep.FINAL || failed) {
        dispatch({ type: Actions.UsersActions.GetAccountInfo });
      }
      refreshChallenges();

      const completedSteps =
        failed || challenge.status === ChallengeStatus.COMPLETED
          ? completedStepsAtLaunchRef.current
          : Math.min(completedStepsAtLaunchRef.current + 1, 7);

      setCompletionResult({
        challenge,
        challengeStep,
        playSessionId,
        ...getCompletionMetricValues(challengeStep, results),
        completedSteps,
        failed,
      });
    },
    onError: () => {
      Events.app.setBootcampHeaderAction.emit("skip");
      setIsWebInputBlocked(false);
      resetActiveSession();
    },
  });

  const startChallenge = (challenge: ChallengeResponse, completedSteps: number) => {
    if (isCreatingSessionRef.current || activeChallengeRef.current) return;

    Events.app.setBootcampHeaderAction.emit("skip");
    isCreatingSessionRef.current = true;
    activeChallengeRef.current = challenge;
    completedStepsAtLaunchRef.current = completedSteps;
    setIsWebInputBlocked(true);
    dispatch({
      type: Actions.SessionActions.CreateChallengeSession,
      payload: {
        account_id: globalState.account?.id || null,
        challenge_id: challenge.id,
      },
      onSuccess: response => {
        isCreatingSessionRef.current = false;
        if (!isMountedRef.current) {
          resetActiveSession();
          return;
        }

        activePlaySessionIdRef.current = response.play_session_id;

        UnityBridge.dispatchWebEvent({
          type: UnityBridge.OutgoingWebEvents.StartPlaylist,
          payload: {
            items: response.play_items,
            play_session_id: response.play_session_id,
          },
        });
      },
      onError: () => {
        Events.app.setBootcampHeaderAction.emit("skip");
        isCreatingSessionRef.current = false;
        setIsWebInputBlocked(false);
        resetActiveSession();
      },
    });
  };

  const closeCompletionResult = () => {
    resetActiveSession();
    setCompletionResult(null);
  };

  const viewFullResults = () => {
    const result = completionResult;
    resetActiveSession();
    setCompletionResult(null);
    if (!result) return;

    Events.app.navigate.emit({
      path: `/activity/session/${result.playSessionId}`,
      data: {
        tags: result.challenge.tags,
        bootcampStep: result.challengeStep,
        bootcampComplete: result.challengeStep === BootcampChallengeStep.FINAL && !result.failed,
        hideHeader: true,
      },
    });
  };

  const retryChallenge = () => {
    const result = completionResult;
    Events.app.setBootcampHeaderAction.emit("skip");
    setCompletionResult(null);
    resetActiveSession();
    if (result) startChallenge(result.challenge, result.completedSteps);
  };

  return {
    completionResult,
    isCompletingSession,
    isWebInputBlocked,
    startChallenge,
    closeCompletionResult,
    viewFullResults,
    retryChallenge,
  };
};

export default useBootcampSession;
